import { INSTANCE as ApplicationManager } from '@core/application/ApplicationManager'
import type { AppInfo } from '@core/application/ApplicationManager'
import type { MicroApp } from 'qiankun'
import { AppCategory } from '@core/application/ApplicationConstants'

export interface AppMenuItem extends AppInfo {
  microApp?: MicroApp | null
}

export interface AppMenuInfo {
  name?: string
  category: AppCategory
  appMenuList: AppMenuItem[]
  haveMoreThanOneSubMenuItems: boolean
}

export class MenuManager {
  #appMenu: Map<string, AppMenuItem> = new Map()
  #initialized = false

  init(appList: AppInfo[]) {
    if (this.#initialized) return

    for (const appItem of appList) {
      this.#appMenu.set(
        appItem.id,
        new Proxy(
          { ...(appItem.hasSubNavigation ? { microApp: null } : {}), ...appItem },
          {
            get(target, property: keyof AppMenuItem) {
              if (property === 'name') {
                return appItem.name
              }

              return target[property]
            },
          }
        )
      )
    }

    this.#initialized = true
  }

  /**
   * return all micro appMenuList
   *
   * @return AppMenuInfo[]
   */
  getMenuList(): AppMenuInfo[] {
    let menuList: AppMenuInfo[] = []
    const appMenuData: Map<AppCategory, AppMenuInfo> = Array.from(this.#appMenu.values())
      .filter((app) => app.category !== AppCategory.SYSTEM)
      .reduce((prev: Map<AppCategory, AppMenuInfo>, current: AppMenuItem) => {
        const { category } = current
        const appMenuInfo = prev.get(category)
        const isPlaceHolderApplication = ApplicationManager.isPlaceHolderApplication(current.id)

        if (appMenuInfo) {
          appMenuInfo.haveMoreThanOneSubMenuItems = true

          !isPlaceHolderApplication && appMenuInfo.appMenuList.push(current)
        } else {
          prev.set(category, {
            category,
            appMenuList: isPlaceHolderApplication ? [] : [current],
            haveMoreThanOneSubMenuItems: false,
          })
        }

        return prev
      }, new Map())

    menuList = Array.from(appMenuData.values())

    return menuList
  }

  async destroy() {
    for (const appMenuItem of this.#appMenu.values()) {
      await appMenuItem.microApp?.unmount()
    }
    this.#appMenu.clear()
  }

  setMenuMicroApp(id: string, microApp: MicroApp) {
    const appMenuItem = this.#appMenu.get(id)

    if (appMenuItem) {
      appMenuItem.microApp = microApp
    }
  }
}

const INSTANCE = new MenuManager()

export { INSTANCE }
